/**
 * Estado financeiro de um agendamento (exame, consulta, procedimento) a partir da
 * cobrança ÂNCORA e das cobranças complementares penduradas nela.
 *
 * Espelha `apps/backend/src/libs/common/billing-settlement/appointment-billing-state.ts`
 * — o backend é quem decide de verdade; aqui só evitamos oferecer no menu uma ação
 * que ele recusaria. Mantenha os dois em sincronia.
 *
 * Existe porque a pergunta "posso gerar cobrança para este atendimento?" era
 * respondida de três jeitos diferentes nas três telas de detalhe, e os três
 * erravam: exame e consulta escondiam a ação só porque `billing` existia, o que
 * travava o atendimento cuja única cobrança havia sido CANCELADA ou ESTORNADA —
 * nada devia, e ninguém podia cobrar.
 */

/**
 * Status em que a cobrança está ENCERRADA sem dinheiro retido — logo, não impede
 * uma nova cobrança para o mesmo atendimento.
 *
 * É uma ALLOWLIST de propósito: status novo passa a BLOQUEAR por padrão, o lado
 * seguro do erro. Ficam de fora `OVERDUE` (viva, só vencida), `IN_DISPUTE`
 * (chargeback pode voltar para paga) e `PARTIALLY_REFUNDED` (parte do dinheiro
 * ficou retida).
 */
export const CLOSED_BILLING_STATUSES = ['CANCELLED', 'REFUNDED', 'WRITTEN_OFF']

export function isClosedBillingStatus(status?: string | null): boolean {
    return !!status && CLOSED_BILLING_STATUSES.includes(status)
}

export interface AppointmentChargeLike {
    id?: number
    amount?: number | null
    /**
     * Taxa repassada ao cliente, JÁ SOMADA em `amount` — o total do atendimento
     * (`finalPrice`) não a inclui. Sem ela na conta, o "Falta" encolhe pelo valor
     * da taxa a cada parcela paga.
     */
    feeAmount?: number | null
    paidAmount?: number | null
    refundedAmount?: number | null
    status?: string | null
}

export interface AppointmentAnchorLike extends AppointmentChargeLike {
    adjustments?: AppointmentChargeLike[] | null
}

export interface AppointmentBillingState {
    /** Soma das cobranças NÃO encerradas — o que ainda está de pé como dívida. */
    billedSoFar: number
    /** Dinheiro que entrou e ficou (pago menos estornado). */
    paidSoFar: number
    /** Existe alguma cobrança viva (não encerrada)? */
    hasLiveBilling: boolean
    /** Quanto ainda pode ser cobrado sem passar do total do atendimento. */
    remainingToBill: number
    /** Quanto ainda falta ENTRAR para quitar o atendimento. */
    remainingToPay: number
    /** Pode gerar uma nova cobrança agora? */
    canGenerate: boolean
    /** A nova cobrança viraria a âncora (cheia) em vez de um complemento? */
    shouldBecomeAnchor: boolean
}

/** Parte da cobrança que é serviço — sem a taxa repassada ao cliente. */
function serviceAmountOf(charge: AppointmentChargeLike): number {
    return Math.max(0, (charge.amount ?? 0) - (charge.feeAmount ?? 0))
}

/**
 * Quanto do SERVIÇO esta cobrança quitou: o que entrou, menos o estornado,
 * descontada a taxa em proporção ao pago. Espelha `servicePaidOf` do backend
 * (`libs/common/billing-settlement/service-amount.ts`).
 */
function netPaidValue(charge: AppointmentChargeLike): number {
    const amount = charge.amount ?? 0
    const paid = charge.paidAmount ?? (charge.status === 'PAID' ? amount : 0)
    const net = Math.max(0, paid - (charge.refundedAmount ?? 0))
    if (net <= 0) return 0

    const service = serviceAmountOf(charge)
    if (amount <= 0) return net
    if (net >= amount) return service

    return Math.round((service * net) / amount)
}

export function resolveAppointmentBillingState(
    anchor: AppointmentAnchorLike | null | undefined,
    finalPrice: number,
): AppointmentBillingState {
    const charges: AppointmentChargeLike[] = anchor
        ? [anchor, ...(anchor.adjustments ?? [])]
        : []
    const live = charges.filter(c => !isClosedBillingStatus(c.status))

    // Base de SERVIÇO: comparado contra `finalPrice`, que não tem taxa dentro.
    const billedSoFar = live.reduce((sum, c) => sum + serviceAmountOf(c), 0)
    const paidSoFar = charges.reduce((sum, c) => sum + netPaidValue(c), 0)
    const total = finalPrice ?? 0

    return {
        billedSoFar,
        paidSoFar,
        hasLiveBilling: live.length > 0,
        remainingToBill: Math.max(0, total - billedSoFar),
        remainingToPay: Math.max(0, total - paidSoFar),
        canGenerate: total > 0 && billedSoFar < total,
        shouldBecomeAnchor: !anchor || (live.length === 0 && paidSoFar === 0),
    }
}

/**
 * O atendimento está, para efeito de cobrança, como se não tivesse nenhuma: ou
 * nunca teve, ou todas as que teve morreram (cancelada, estornada, baixada) sem
 * deixar dinheiro retido.
 *
 * Espelha `hasNoEffectiveBilling` do backend — é o critério que libera a edição a
 * gerar cobrança nova e, com ele, a trocar o CONVÊNIO (que decide o pagador dela).
 */
export function hasNoEffectiveBilling(
    anchor: AppointmentAnchorLike | null | undefined,
): boolean {
    return resolveAppointmentBillingState(anchor, 0).shouldBecomeAnchor
}
